import { useState } from 'react';
import { apiFetch } from '../lib/api';
import { DeletionResidueNotice } from './DeletionResidueNotice';

export function PrivacyPage() {
  const [copied, setCopied] = useState(false);
  const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm' | 'deleting' | 'done'>('idle');
  const [confirmText, setConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const copyEmail = () => {
    navigator.clipboard.writeText('privacy@terum.ai');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    setDeleteStep('deleting');
    setDeleteError('');
    try {
      await apiFetch<{ deleted: boolean }>('/api/account/delete-data', { method: 'POST' });
      setDeleteStep('done');
    } catch {
      setDeleteError('Failed to delete data. Please try again or contact privacy@terum.ai.');
      setDeleteStep('confirm');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      color: 'var(--text2)',
      padding: '64px 24px',
      fontFamily: 'var(--sans)',
      fontSize: 15,
      lineHeight: 1.7,
    }}>
      <article style={{ maxWidth: 640, margin: '0 auto' }}>
        <header style={{ marginBottom: 48 }}>
          <h1 style={{ fontSize: 32, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--display)', marginBottom: 8 }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text3)' }}>Last updated: May 22, 2026</p>
        </header>

        <Section title="What Terum does">
          <p>
            Terum is a browser extension and web service that captures your AI
            conversation history, builds a personal knowledge graph, and injects
            relevant context back into your AI tools. This policy explains what
            data we collect, why, and how you control it.
          </p>
        </Section>

        <Section title="Data we collect">
          <ul style={{ listStyle: 'disc', paddingLeft: 20 }}>
            <Li label="AI conversation content">
              When you use ChatGPT, Claude, or Perplexity with the extension
              active, we capture the prompts you send and the responses you
              receive. You can pause capture per-site at any time from the
              extension popup.
            </Li>
            <Li label="Conversation history (backfill)">
              With your explicit action, the extension can sync up to 30 days of
              past conversations from ChatGPT and Claude. This uses your existing
              browser session and requires you to be logged in to those services.
            </Li>
            <Li label="LinkedIn profile data">
              If you choose to connect LinkedIn, we read your public profile
              information (skills, experience, education) using your existing
              browser session. We do not access your connections, messages, or
              activity feed.
            </Li>
            <Li label="GitHub activity">
              If you connect GitHub via OAuth, we read your public commits,
              issues, and pull requests. We request only the scopes necessary for
              read access.
            </Li>
            <Li label="Account information">
              Your email address and name from Google sign-in, used for
              authentication.
            </Li>
            <Li label="Usage metadata">
              Capture counts, enrichment injection events (site, latency, item
              count), and sync progress. We do not track browsing history or
              activity outside of the supported AI platforms.
            </Li>
          </ul>
        </Section>

        <Section title="How we use your data">
          <ul style={{ listStyle: 'disc', paddingLeft: 20 }}>
            <li style={{ marginBottom: 12 }}>
              Build your personal knowledge graph — extracting skills, tools,
              topics, preferences, and relationships from your conversations.
            </li>
            <li style={{ marginBottom: 12 }}>
              Inject relevant context into your AI prompts so your AI tools
              understand your background without you repeating yourself.
            </li>
            <li style={{ marginBottom: 12 }}>
              Power the Terum dashboard, including identity surfaces, memory
              views, and the Hero Chat assistant.
            </li>
          </ul>
          <p>
            We do not sell your data. We do not use your conversation content to
            train AI models. Your data is used solely to provide the Terum
            service to you.
          </p>
        </Section>

        <Section title="Third-party services">
          <p>We use the following services to operate Terum:</p>
          <ul style={{ listStyle: 'disc', paddingLeft: 20 }}>
            <Li label="Supabase">Authentication and database hosting.</Li>
            <Li label="OpenAI">
              Generating embeddings for knowledge graph construction.
              Conversation fragments are sent to the OpenAI embeddings API for
              semantic processing.
            </Li>
            <Li label="Anthropic and Google AI">
              Powering the Hero Chat assistant when you use it on the Terum
              dashboard.
            </Li>
          </ul>
        </Section>

        <Section title="Data storage and security">
          <p>
            Your data is stored in encrypted-at-rest databases. Authentication
            tokens are stored locally in your browser's extension storage and are
            never transmitted to third parties. All communication between the
            extension and our servers uses HTTPS.
          </p>
        </Section>

        <Section title="Your controls">
          <ul style={{ listStyle: 'disc', paddingLeft: 20 }}>
            <Li label="Pause capture">
              Toggle capture off globally or per-site from the extension popup at
              any time.
            </Li>
            <Li label="Disconnect">
              Use the "Disconnect extension" button in the popup to clear all
              locally stored tokens and stop all data collection.
            </Li>
            <Li label="Delete your data">
              {deleteStep === 'idle' && (
                <>
                  {/* This control POSTs /api/account/delete-data, which erases your data and
                      deliberately PRESERVES the account (the success line below has always said so).
                      It used to be labelled "Delete my account and data", which promised something
                      it does not do — a mislabeled destructive control on a privacy page. Account
                      deletion itself lives in Settings; the label now matches the endpoint. */}
                  You can permanently delete all the data Terum holds about you. Your account stays
                  open — you can delete the account itself from Settings. This action is
                  irreversible.{' '}
                  <button
                    onClick={() => setDeleteStep('confirm')}
                    style={{
                      color: '#ff6b8a', textDecoration: 'underline', background: 'none',
                      border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit',
                    }}
                  >
                    Delete all my data
                  </button>
                </>
              )}
              {deleteStep === 'confirm' && (
                <div style={{
                  marginTop: 12, padding: 16, borderRadius: 8,
                  background: 'rgba(255,107,138,0.06)', border: '1px solid rgba(255,107,138,0.2)',
                }}>
                  <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500, marginBottom: 8 }}>
                    This will permanently delete:
                  </div>
                  <ul style={{ listStyle: 'disc', paddingLeft: 20, fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
                    <li>All captured conversations</li>
                    <li>Your knowledge graph and memory</li>
                    <li>Chat history, integrations, and settings</li>
                  </ul>
                  <div style={{ marginBottom: 12 }}>
                    <DeletionResidueNotice />
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>
                    Type <strong style={{ color: 'var(--text)' }}>delete my data</strong> to confirm:
                  </div>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={e => setConfirmText(e.target.value)}
                    placeholder="delete my data"
                    style={{
                      width: '100%', padding: '8px 12px', background: 'var(--bg2)',
                      border: '1px solid var(--border-b)', borderRadius: 6,
                      color: 'var(--text)', fontFamily: 'var(--sans)', fontSize: 13,
                      marginBottom: 12,
                    }}
                  />
                  {deleteError && (
                    <div style={{ fontSize: 13, color: '#ff6b8a', marginBottom: 10 }}>{deleteError}</div>
                  )}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={handleDelete}
                      disabled={confirmText !== 'delete my data'}
                      style={{
                        padding: '8px 20px', borderRadius: 5, border: 'none', cursor: 'pointer',
                        fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
                        background: confirmText === 'delete my data' ? '#ff6b8a' : 'var(--bg2)',
                        color: confirmText === 'delete my data' ? '#fff' : 'var(--text3)',
                        opacity: confirmText === 'delete my data' ? 1 : 0.5,
                      }}
                    >
                      DELETE ALL MY DATA
                    </button>
                    <button
                      onClick={() => { setDeleteStep('idle'); setConfirmText(''); setDeleteError(''); }}
                      style={{
                        padding: '8px 20px', borderRadius: 5, background: 'none',
                        border: '1px solid var(--border)', cursor: 'pointer',
                        fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
                        color: 'var(--text3)',
                      }}
                    >
                      CANCEL
                    </button>
                  </div>
                </div>
              )}
              {deleteStep === 'deleting' && (
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text2)' }}>
                  Deleting all your data...
                </div>
              )}
              {deleteStep === 'done' && (
                <div style={{
                  marginTop: 12, padding: '12px 16px', borderRadius: 6,
                  background: 'rgba(0,232,157,0.08)', border: '1px solid rgba(0,232,157,0.2)',
                  fontSize: 13, color: '#00e89d',
                }}>
                  All data deleted. Your account is still active — delete it from Settings if you
                  want it gone too.
                </div>
              )}
            </Li>
          </ul>
        </Section>

        <Section title="Data we do not collect">
          <ul style={{ listStyle: 'disc', paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}>Browsing history or activity outside ChatGPT, Claude, and Perplexity.</li>
            <li style={{ marginBottom: 8 }}>Passwords or financial information.</li>
            <li style={{ marginBottom: 8 }}>Content from other browser tabs or applications.</li>
            <li style={{ marginBottom: 8 }}>
              Cookies or session tokens from third-party sites (these are used
              locally by the extension for API access and never transmitted to
              Terum servers).
            </li>
          </ul>
        </Section>

        <Section title="Changes">
          <p>
            We may update this policy as Terum evolves. Significant changes will
            be communicated through the product. The "last updated" date at the
            top reflects the most recent revision.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about this policy? Reach us at{' '}
            <button onClick={copyEmail} style={{ color: 'var(--accent2)', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}>
              {copied ? 'Copied!' : 'privacy@terum.ai'}
            </button>
            .
          </p>
        </Section>
      </article>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>{title}</h2>
      {children}
    </section>
  );
}

function Li({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <li style={{ marginBottom: 12 }}>
      <strong style={{ color: 'var(--text)' }}>{label}.</strong>{' '}
      {children}
    </li>
  );
}
